import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Video, Circle, Square, RotateCcw, Check, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 600; // 10 minutes

// In-app camera recorder (TikTok-style). Records up to 10 min and returns a File.
export const CameraRecorder = ({ open, onOpenChange, onRecorded }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [facing, setFacing] = useState("user");
  const [ready, setReady] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(async (facingMode) => {
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (e) {
      setReady(false);
      toast.error("Caméra/micro inaccessibles. Autorisez l'accès dans votre navigateur.");
    }
  }, [stopStream]);

  useEffect(() => {
    if (open) {
      setElapsed(0);
      setRecording(false);
      startStream(facing);
    } else {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (!open) return;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const pickMime = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    for (const c of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported(c)) return c;
    }
    return "";
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickMime();
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    } catch (e) {
      toast.error("Enregistrement non supporté sur cet appareil.");
      return;
    }
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const type = mime || "video/webm";
      const blob = new Blob(chunksRef.current, { type });
      const ext = type.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `enregistrement-${Date.now()}.${ext}`, { type });
      onRecorded?.(file);
      stopStream();
      onOpenChange(false);
    };
    recorderRef.current = rec;
    rec.start(1000);
    setRecording(true);
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((s) => {
        const next = s + 1;
        if (next >= MAX_SECONDS) {
          toast.info("Durée maximale de 10 minutes atteinte.");
          stopRecording();
        }
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
    try { recorderRef.current?.stop(); } catch (e) {}
  };

  const flipCamera = () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    startStream(next);
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopRecording(); stopStream(); } onOpenChange(v); }}>
      <DialogContent className="max-w-sm rounded-2xl p-0 overflow-hidden" data-testid="camera-recorder">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle className="font-display flex items-center gap-2"><Video className="h-5 w-5 text-primary" /> Enregistrer une vidéo</DialogTitle>
        </DialogHeader>
        <div className="relative bg-black aspect-[9/16] max-h-[60vh] w-full">
          <video ref={videoRef} playsInline className="w-full h-full object-cover" data-testid="camera-preview" />
          {recording && (
            <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs text-white">
              <span className="h-2.5 w-2.5 rounded-full bg-[hsl(0_92%_62%)] animate-pulse" />
              <span className="tabular-nums" data-testid="camera-timer">{mmss(elapsed)}</span>
              <span className="text-white/50">/ 10:00</span>
            </div>
          )}
          {ready && !recording && (
            <button onClick={flipCamera} className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-black/50 text-white" data-testid="camera-flip" aria-label="Changer de caméra">
              <SwitchCamera className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="flex items-center justify-center gap-4 p-4">
          {!recording ? (
            <Button onClick={startRecording} disabled={!ready} className="rounded-full h-14 w-14 p-0 bg-[hsl(0_92%_62%)] hover:bg-[hsl(0_92%_56%)]" data-testid="camera-start" aria-label="Démarrer l'enregistrement">
              <Circle className="h-7 w-7 fill-white text-white" />
            </Button>
          ) : (
            <Button onClick={stopRecording} className="rounded-full h-14 w-14 p-0" data-testid="camera-stop" aria-label="Arrêter">
              <Square className="h-6 w-6 fill-current" />
            </Button>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground text-center pb-4 px-4">Durée maximale : 10 minutes. Appuyez pour démarrer / arrêter.</p>
      </DialogContent>
    </Dialog>
  );
};

export default CameraRecorder;
